/**
 * Conduces x Mayor — listado de las entregas en consignación a clientes.
 *
 * Un conduce no es una factura: son motores que el cliente se llevó a vender.
 * El listado ordena por lo que exige acción — el chip "Por facturar" marca los
 * conduces donde el cliente ya vendió y todavía no se le ha emitido factura.
 *
 * Desde aquí se abre el detalle (conducedetallexmayor.js), que es donde se
 * marca lo vendido, se factura, se devuelve lo que quedó en piso y se consultan
 * las facturas que salieron del conduce.
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, StatusPill, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { estadoConduce } from "lib/conducesxmayor";
import ConduceDetalleXmayor from "modules/ventasxmayor/conducedetallexmayor";
import { fmtMoney, monedaLabel, imprimirConduce } from "modules/ventasxmayor/impresionxmayor";

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioAnioISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin conduces en el rango
      </Typography>
    </Stack>
  );
}

export default function ConducesXmayor({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  // El rango arranca en el año porque un conduce puede quedar abierto meses:
  // acotarlo al mes en curso esconde justo los que llevan tiempo sin venderse.
  const [fecha1, setFecha1] = useState(inicioAnioISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [estado, setEstado] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargarClientes = async () => {
    const { ok, data } = await post("/api/xmayor_clientes", {});
    if (ok) setClientes(data.clientes || []);
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const body = { fechadesde: fecha1, fechahasta: fecha2 };
      if (cliente) body.cliente = cliente.id;
      if (estado) body.estado = estado;
      const { ok, data } = await post("/api/conducexmayor_listar", body);
      if (ok) {
        setRows(data.conduces || []);
      } else {
        showAlert({ message: data.Error || "No se pudieron listar los conduces", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarClientes();
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const porFacturar = useMemo(
    () => rows.filter((r) => r.estado === "porfacturar").length,
    [rows]
  );

  const [detalleId, setDetalleId] = useState(null);
  const [imprimiendo, setImprimiendo] = useState(0);

  const imprimir = async (row) => {
    setImprimiendo(row.id);
    try {
      const { ok, data } = await post("/api/conducexmayor_detalle", { id: row.id });
      if (ok) {
        imprimirConduce(data);
      } else {
        showAlert({ message: data.Error || "No se pudo cargar el conduce", color: "error" });
      }
    } finally {
      setImprimiendo(0);
    }
  };

  const columns = [
    { field: "id", headerName: "Cond#", width: 80 },
    { field: "fechatexto", headerName: "Fecha", width: 110 },
    {
      // Días desde la entrega mientras el conduce sigue activo (en piso o por
      // facturar); facturado o anulado ya no corre. Lo calcula el servidor.
      field: "diasactivo",
      headerName: "Tiempo activo",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const d = params.row.diasactivo;
        if (d === null || d === undefined) return "—";
        if (Number(d) === 0) return "Hoy";
        return Number(d) === 1 ? "1 día" : `${d} días`;
      },
    },
    { field: "clientenombre", headerName: "Cliente", flex: 1, minWidth: 180 },
    {
      field: "motores",
      headerName: "Motores",
      width: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "enpiso",
      headerName: "En piso",
      width: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "vendidos",
      headerName: "Por facturar",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        Number(params.row.vendidos) > 0 ? (
          <Typography sx={{ fontWeight: 800, color: "#E65100", fontSize: "0.85rem" }}>
            {params.row.vendidos}
          </Typography>
        ) : (
          "—"
        ),
    },
    {
      field: "facturados",
      headerName: "Facturados",
      width: 105,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        Number(params.row.facturas) > 0
          ? `${params.row.facturados} (${params.row.facturas} fact.)`
          : params.row.facturados,
    },
    {
      field: "valorpiso",
      headerName: "En piso",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "valorfacturado",
      headerName: "Facturado",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const { label, variant } = estadoConduce(params.row);
        return <StatusPill label={label} variant={variant} />;
      },
    },
    {
      field: "acciones",
      headerName: "",
      width: 230,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setDetalleId(params.row.id)}
            sx={btnOutline(FP.color.accentBlue)}
          >
            Detalle
          </Button>
          <Button
            size="small"
            startIcon={
              imprimiendo === params.row.id ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <PrintOutlinedIcon />
              )
            }
            onClick={() => imprimir(params.row)}
            disabled={imprimiendo === params.row.id}
            sx={btnOutline(FP.color.navy)}
          >
            Imprimir
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box
      component={embedded ? "section" : "main"}
      sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      {!embedded && (
        <PageHeader
          title="Conduces x Mayor"
          subtitle="Motores entregados en consignación: marque lo vendido y facture solo esos"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
              <Autocomplete
                autoHighlight
                options={clientes}
                value={cliente}
                getOptionLabel={(o) => (typeof o === "string" ? o : o.descrip || "")}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(_, v) => setCliente(v)}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Estado"
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="porfacturar">Por facturar</MenuItem>
                <MenuItem value="enpiso">En piso</MenuItem>
                <MenuItem value="facturado">Facturado</MenuItem>
                <MenuItem value="anulado">Anulado</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}
            >
              {porFacturar > 0 && (
                <Typography sx={{ fontWeight: 800, color: "#E65100", fontSize: "0.85rem" }}>
                  {porFacturar} conduce(s) con ventas por facturar
                </Typography>
              )}
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} conduces
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
          <Typography sx={{ mt: 1.2, fontSize: "0.76rem", color: FP.color.textMuted }}>
            Las facturas que salen de un conduce se consultan dentro de su detalle; no aparecen en
            el listado de Facturas x Mayor, pero sí en cobros, antigüedad de saldo y estado de
            cuenta del cliente.
          </Typography>
        </Box>
      </Box>

      {/* Detalle del conduce */}
      <Dialog fullScreen open={!!detalleId} onClose={() => setDetalleId(null)}>
        {detalleId && (
          <ConduceDetalleXmayor
            conduceId={detalleId}
            onExit={() => {
              setDetalleId(null);
              cargar();
            }}
            onChanged={cargar}
          />
        )}
      </Dialog>
    </Box>
  );
}
